import React, { useState, useEffect } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { Search, Edit2, Trash2, Users, Shield, Crown, TrendingUp, AlertCircle, PlusCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { useBodyLock } from '../hooks/useBodyLock';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [llmCosts, setLlmCosts] = useState(null);

  // Body lock pour les modals
  useBodyLock(showEditModal || showDeleteModal || showCreditsModal);

  const pageSize = 20;

  // Fetch stats + LLM costs
  useEffect(() => {
    fetchStats();
    fetchLlmCosts();
  }, []);

  // Fetch users
  useEffect(() => {
    fetchUsers();
  }, [page, search, roleFilter, statusFilter]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users/stats/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLlmCosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/llm-costs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLlmCosts(data);
      }
    } catch (error) {
      console.error('Error fetching LLM costs:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });

      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('subscription_status', statusFilter);

      const response = await fetch(`${BACKEND_URL}/api/admin/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(Math.ceil(data.total / pageSize));
      } else {
        toast.error('Erreur lors du chargement des utilisateurs');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        toast.success('Utilisateur mis à jour');
        fetchUsers();
        fetchStats();
        setShowEditModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erreur de connexion');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Utilisateur supprimé');
        fetchUsers();
        fetchStats();
        setShowDeleteModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur de connexion');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-800',
      affiliate: 'bg-purple-100 text-purple-800',
      user: 'bg-gray-100 text-gray-800'
    };
    const icons = {
      admin: <Shield className="w-3 h-3" />,
      affiliate: <TrendingUp className="w-3 h-3" />,
      user: <Users className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[role] || styles.user}`}>
        {icons[role]}
        {role}
      </span>
    );
  };

  const getStatusBadge = (status, plan) => {
    if (plan === 'premium' && status === 'active') {
      return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
        <Crown className="w-3 h-3" />
        Premium
      </span>;
    }
    if (status === 'trialing') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Essai</span>;
    }
    if (status === 'canceled' || status === 'expired') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Inactif</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Gratuit</span>;
  };

  return (
    <div className="p-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Utilisateurs</p>
                <p className="text-2xl font-bold">{stats.total_users}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Premium</p>
                <p className="text-2xl font-bold text-amber-600">{stats.premium_users}</p>
              </div>
              <Crown className="w-8 h-8 text-amber-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En Essai</p>
                <p className="text-2xl font-bold text-blue-600">{stats.trialing_users}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Affiliés</p>
                <p className="text-2xl font-bold text-purple-600">{stats.affiliates}</p>
              </div>
              <Shield className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4" data-testid="llm-cost-month-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">LLM / Mois</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ${llmCosts ? llmCosts.global_month.toFixed(2) : '—'}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4" data-testid="llm-cost-year-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">LLM / Année</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ${llmCosts ? llmCosts.global_year.toFixed(2) : '—'}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-400" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par email ou nom..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les rôles</option>
            <option value="user">Utilisateur</option>
            <option value="admin">Admin</option>
            <option value="affiliate">Affilié</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="trialing">En essai</option>
            <option value="inactive">Inactif</option>
            <option value="canceled">Annulé</option>
          </select>

          <button
            onClick={() => {
              setSearch('');
              setRoleFilter('');
              setStatusFilter('');
              setPage(1);
            }}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Aucun utilisateur trouvé</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sessions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coût LLM</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filleuls</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inscrit le</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{user.name || 'Sans nom'}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(user.subscription_status, user.plan)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {user.sessions_current != null ? (user.sessions_current + (user.sessions_bonus || 0)) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm" data-testid={`llm-cost-${user.id}`}>
                        <span className={`inline-flex items-center gap-1 font-medium ${
                          user.llm_cost_month_usd > 1 ? 'text-red-600' :
                          user.llm_cost_month_usd > 0.1 ? 'text-amber-600' :
                          'text-gray-500'
                        }`}>
                          <DollarSign className="w-3 h-3" />
                          {user.llm_cost_month_usd != null ? user.llm_cost_month_usd.toFixed(4) : '0.0000'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {user.referrals_count || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          data-testid={`add-credits-btn-${user.id}`}
                          onClick={() => {
                            setSelectedUser(user);
                            setShowCreditsModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 mr-3"
                          title="Ajouter des sessions"
                        >
                          <PlusCircle className="w-5 h-5" />
                        </button>
                        <button
                          data-testid={`edit-user-btn-${user.id}`}
                          onClick={() => {
                            setSelectedUser(user);
                            setShowEditModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          data-testid={`delete-user-btn-${user.id}`}
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {page} sur {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateUser}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedUser && (
        <DeleteUserModal
          user={selectedUser}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => handleDeleteUser(selectedUser.id)}
        />
      )}

      {/* Add Credits Modal */}
      {showCreditsModal && selectedUser && (
        <AddCreditsModal
          user={selectedUser}
          onClose={() => setShowCreditsModal(false)}
          onSuccess={() => {
            setShowCreditsModal(false);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ user, onClose, onSave }) => {
  const [role, setRole] = useState(user.role);
  const [plan, setPlan] = useState(user.plan);
  const [status, setStatus] = useState(user.subscription_status);

  const handleSave = () => {
    const updates = {};
    if (role !== user.role) updates.role = role;
    if (plan !== user.plan) updates.plan = plan;
    if (status !== user.subscription_status) updates.subscription_status = status;

    if (Object.keys(updates).length > 0) {
      onSave(user.id, updates);
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4">Modifier l'utilisateur</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="text"
              value={user.email}
              disabled
              className="w-full px-3 py-2 border rounded-lg bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="user">Utilisateur</option>
              <option value="admin">Admin</option>
              <option value="affiliate">Affilié</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="free">Gratuit</option>
              <option value="premium">Premium</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut Abonnement</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="inactive">Inactif</option>
              <option value="trialing">En essai</option>
              <option value="active">Actif</option>
              <option value="canceled">Annulé</option>
              <option value="expired">Expiré</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete User Modal Component
const DeleteUserModal = ({ user, onClose, onConfirm }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-xl font-bold">Supprimer l'utilisateur</h3>
        </div>
        
        <p className="text-gray-600 mb-6">
          Êtes-vous sûr de vouloir supprimer <strong>{user.email}</strong> ?
          Cette action est irréversible et supprimera toutes les données associées.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Credits Modal Component
const AddCreditsModal = ({ user, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/admin/users/${user.id}/balance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          setBalance(await response.json());
        }
      } catch (err) {
        console.error('Error fetching balance:', err);
      } finally {
        setLoadingBalance(false);
      }
    };
    fetchBalance();
  }, [user.id]);

  const handleSubmit = async () => {
    const parsedAmount = parseInt(amount, 10);
    if (!parsedAmount || parsedAmount <= 0) {
      toast.error('Le nombre de sessions doit être supérieur à 0');
      return;
    }
    if (!reason.trim()) {
      toast.error('Le motif est requis');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${user.id}/credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount: parsedAmount, reason: reason.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.amount_added} sessions ajoutées (solde: ${data.balance_after})`);
        onSuccess();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de l\'ajout');
      }
    } catch (err) {
      console.error('Error adding credits:', err);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const parsedAmount = parseInt(amount, 10) || 0;
  const projectedTotal = balance ? balance.total_available + parsedAmount : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" data-testid="add-credits-modal">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <PlusCircle className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-xl font-bold">Ajouter des sessions</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur</label>
            <input
              type="text"
              value={user.email}
              disabled
              className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-sm"
            />
          </div>

          {loadingBalance ? (
            <div className="text-sm text-gray-500">Chargement du solde...</div>
          ) : balance ? (
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Sessions mensuelles</span>
                <span className="font-medium" data-testid="balance-current">{balance.sessions_current}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Sessions bonus</span>
                <span className="font-medium" data-testid="balance-bonus">{balance.sessions_bonus}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold border-t pt-1 mt-1">
                <span>Solde total</span>
                <span data-testid="balance-total">{balance.total_available}</span>
              </div>
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de sessions à ajouter</label>
            <input
              data-testid="credits-amount-input"
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Ex: 50"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motif</label>
            <textarea
              data-testid="credits-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Compensation suite à un incident technique"
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          {parsedAmount > 0 && balance && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex justify-between text-sm font-semibold text-green-800">
                <span>Solde projeté après ajout</span>
                <span data-testid="projected-total">{projectedTotal}</span>
              </div>
              <div className="text-xs text-green-600 mt-1">
                +{parsedAmount} sessions bonus
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Annuler
          </button>
          <button
            data-testid="confirm-add-credits-btn"
            onClick={handleSubmit}
            disabled={submitting || !parsedAmount || parsedAmount <= 0 || !reason.trim()}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Ajout en cours...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
